import React, { useEffect, useRef, useState } from 'react';
import { Filter, X } from 'lucide-react';
import type { Department } from '../../services/api';

interface DepartmentFilterProps {
  departments: Department[];
  excluded: string[];
  onChange: (excluded: string[]) => void;
}

/**
 * Global department exclusion filter. Checked departments are removed from
 * every period-driven number on the dashboard (KPIs, trend, departments,
 * alerts, recent activity).
 */
export const DepartmentFilter: React.FC<DepartmentFilterProps> = ({ departments, excluded, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = (id: string) => {
    onChange(excluded.includes(id) ? excluded.filter(x => x !== id) : [...excluded, id]);
  };

  const active = excluded.length > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          active
            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        <Filter size={14} />
        {active ? `Excluding ${excluded.length}` : 'Filter'}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl border border-gray-200 shadow-lg z-40 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Exclude departments</span>
            {active && (
              <button onClick={() => onChange([])} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                Clear all
              </button>
            )}
          </div>
          <p className="text-[11px] text-gray-400 mb-2">
            Checked departments are removed from all dashboard numbers — KPIs, trend, mix, and alerts.
          </p>
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {departments.map(d => (
              <label
                key={d.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={excluded.includes(d.id)}
                  onChange={() => toggle(d.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {d.name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Amber strip shown while exclusions are active so numbers aren't misread. */
export const ExclusionBanner: React.FC<{
  departments: Department[];
  excluded: string[];
  onClear: () => void;
}> = ({ departments, excluded, onClear }) => {
  if (excluded.length === 0) return null;
  const names = excluded
    .map(id => departments.find(d => d.id === id)?.name)
    .filter(Boolean)
    .join(', ');
  return (
    <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm">
      <span>
        <span className="font-semibold">Filtered view</span> — excluding {names || `${excluded.length} departments`} from all numbers below (Growth Engines unaffected).
      </span>
      <button onClick={onClear} className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 font-medium flex-shrink-0">
        <X size={14} /> Clear
      </button>
    </div>
  );
};
